import { useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";

import { ProblemError, TakeOverConflict, TakeOverRefused, type FormCandidate, type ReadingReviewOut } from "@/api";
import { Button } from "@/components/Button";
import { PageTitle } from "@/components/PageTitle";
import { StateMessage } from "@/components/StateMessage";
import { pageFrame, usePageImage } from "@/lib/usePageImage";

import { candidateLine, candidateName, formLine, VOTES_NOTE } from "./model";
import { OriginalPane } from "./OriginalPane";
import styles from "./Review.module.css";

/**
 * What this screen draws of a reading: its form readings and its page. A
 * review of a reading has them, and so has the server's `formChoice` — the
 * same fields of the reading a choice rests on, for the way back after the
 * measures of a chosen reading failed (review I-1).
 */
export type FormView = Pick<
  ReadingReviewOut,
  "formStatus" | "candidates" | "pageCount" | "pageWidthPx" | "pageHeightPx" | "simplifications"
>;

interface FormUnsureProps {
  review: FormView;
  pageUrl: string;
  /**
   * „Diese Lesart nehmen — Maße lesen“ (plan 4 §9): asks for a job that
   * reads the measures of the reading at `position` — its place in the list
   * this reading shows (§9.1). Resolves once the job is asked for (the screen
   * then moves on to the progress); throws the refusals.
   */
  onChoose: (position: number) => Promise<void>;
  /** `/umriss?case=…&aus=lesung&kandidat=n` for the reading at `position`. */
  editorHref: (position: number) => string;
  /** `/umriss?case=…` — the outline by hand, for this Vorgang. */
  drawHref: string;
  planningHref: string;
  /** Read the reading and the Vorgang again (a newer reading, a Stand meanwhile). */
  onReload: () => void;
  /** Back to the review of the reading chosen before — only where one was (`gewaehlt`). */
  backHref?: string | null;
  /**
   * „Andere Seite wählen“ (Seitenwahl, Folgepunkt a): where this reading
   * read one page of a longer original — perhaps the wrong one. Not offered
   * from a chosen reading: a person said the form was on this page.
   */
  otherPageHref?: string | null;
  /** „Seite 2 aus „Angebot.pdf““ — the page this reading read, where it is a page of an original. */
  readFrom?: string | null;
  /** The page on the field's head — „Seite 2“ for a page of an original. */
  sheetLabel?: string;
}

type Problem =
  | { kind: "stand" }
  | { kind: "stale" }
  | { kind: "checked" }
  | { kind: "unknown_candidate" }
  | { kind: "other"; detail: string | null };

/** Says that „Ohne Maße im Editor öffnen“ carries no measure (19.10.2026). */
const WITHOUT_MEASURES = "form-unsure-without-measures";

const CARD_W = 160;
const CARD_H = 120;
const CARD_PAD = 12;

/** One reading's outline fitted into its card, in the sheet's own proportion. */
function cardPoints(candidate: FormCandidate, aspect: number): string {
  const points = [...candidate.points, ...(candidate.closed && candidate.points[0] ? [candidate.points[0]] : [])];
  const xs = points.map(([x]) => x * aspect);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX || 1;
  const spanY = Math.max(...ys) - minY || 1;
  const scale = Math.min((CARD_W - 2 * CARD_PAD) / spanX, (CARD_H - 2 * CARD_PAD) / spanY);
  const offsetX = (CARD_W - spanX * scale) / 2;
  const offsetY = (CARD_H - spanY * scale) / 2;
  return points
    .map(([x, y]) => `${(offsetX + (x * aspect - minX) * scale).toFixed(1)},${(offsetY + (y - minY) * scale).toFixed(1)}`)
    .join(" ");
}

function problemOf(cause: unknown): Problem {
  if (cause instanceof TakeOverConflict) {
    if (cause.kind === "stand_exists") return { kind: "stand" };
    if (cause.kind === "stale") return { kind: "stale" };
    if (cause.kind === "form_checked") return { kind: "checked" };
    return { kind: "other", detail: cause.detail };
  }
  if (cause instanceof TakeOverRefused && cause.code === "unknown_candidate") return { kind: "unknown_candidate" };
  if (cause instanceof TakeOverRefused) return { kind: "other", detail: cause.detail };
  if (cause instanceof ProblemError) return { kind: "other", detail: cause.userMessage };
  return { kind: "other", detail: cause instanceof Error ? cause.message : null };
}

/**
 * „Umriss nicht sicher erkannt.“ (plan 4 §5, §9, §10.1) — the readings did
 * not agree on the shape, so no wall is measured yet. The person chooses the
 * reading that matches their sheet; then the machine reads the measures of
 * exactly that form and checks them (product rule §2: a person confirms the
 * form, the machine proposes and checks the measures).
 *
 * **Nothing is chosen for them** (§10.1) — not even the reading with the
 * most votes: in the case that started this, the follow-up question voted
 * twice for the minority. The votes stand at every reading, as how often the
 * machine saw it. Pointing at or focusing a card lays it over the sheet, so
 * the comparison happens where the handwriting is; choosing is a press.
 *
 * The same screen is the way back from a chosen reading („Andere Lesart“,
 * `formStatus` `gewaehlt`): the readings in their usual order, the one that
 * holds now marked as such, and choosing it again is staying.
 *
 * Without a single shape there is nothing to choose: only „Selbst zeichnen“.
 */
export function FormUnsure({
  review,
  pageUrl,
  onChoose,
  editorHref,
  drawHref,
  planningHref,
  onReload,
  backHref = null,
  otherPageHref = null,
  readFrom = null,
  sheetLabel = "Seite 1",
}: FormUnsureProps) {
  const image = usePageImage(pageUrl);
  const again = review.formStatus === "gewaehlt";
  // A chosen reading lists the chosen one first (§9.1); the cards keep the
  // order of the labels, so A stays where A was. `position` is what the
  // server is told.
  const cards = review.candidates
    .map((candidate, position) => ({ candidate, position }))
    .sort((a, b) => a.candidate.label.localeCompare(b.candidate.label));
  const holding = again ? 0 : null;
  const otherPage = again ? null : otherPageHref;
  const total = review.candidates.reduce((sum, candidate) => sum + candidate.votes, 0) || 3;
  const [chosen, setChosen] = useState<number | null>(null);
  const [previewed, setPreviewed] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const cardRefs = useRef(new Map<number, HTMLButtonElement>());
  const frame = pageFrame(review, image);
  const aspect = frame.width > 0 && frame.height > 0 ? frame.width / frame.height : Math.SQRT2;
  const shownPosition = previewed ?? chosen;
  const shown = shownPosition !== null ? (review.candidates[shownPosition] ?? null) : null;
  const chosenCandidate = chosen !== null ? (review.candidates[chosen] ?? null) : null;
  const staying = again && chosen === holding;
  // Tab lands on the chosen card, or on the first one while nothing is
  // chosen — focus alone chooses nothing.
  const tabStop = chosen ?? cards[0]?.position ?? 0;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    const backward = event.key === "ArrowUp" || event.key === "ArrowLeft";
    if (!forward && !backward && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    const count = cards.length;
    const from = cards.findIndex((card) => card.position === (chosen ?? previewed ?? tabStop));
    const at =
      event.key === "Home" ? 0 : event.key === "End" ? count - 1 : (Math.max(from, 0) + (forward ? 1 : -1) + count) % count;
    const next = cards[at]!.position;
    setChosen(next);
    setProblem(null);
    cardRefs.current.get(next)?.focus();
  }

  async function take(): Promise<void> {
    if (chosen === null || sending) return;
    setSending(true);
    setProblem(null);
    try {
      await onChoose(chosen);
      // On success the screen is left for the progress; it stays busy until then.
    } catch (cause) {
      setProblem(problemOf(cause));
      setSending(false);
    }
  }

  const reasonId = "form-choose-reason";
  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        <div className={styles.headRow}>
          <div className={styles.headText}>
            {/* In its own display look also under the frame: frozen with the
                review until 09.11.2026 (M2, final review I-1). */}
            <PageTitle className={styles.title} ownLook>
              {again ? "Andere Lesart wählen" : "Umriss nicht sicher erkannt."}
            </PageTitle>
            <p className={styles.count}>
              {again
                ? `Gerade gilt ${candidateName(review.candidates[0]!)}. Nimmst du eine andere, werden ihre Maße gelesen.`
                : formLine(review.candidates)}
            </p>
          </div>
          {again && backHref !== null ? (
            <div className={styles.actions}>
              <Link className={styles.secondaryLink} to={backHref}>
                Zurück zur Prüfung
              </Link>
            </div>
          ) : null}
        </div>
        {readFrom !== null ? (
          <p className={styles.hints}>Gelesen: {readFrom}.</p>
        ) : review.pageCount > 1 ? (
          <p className={styles.hints}>Seite 1 von {review.pageCount} gelesen.</p>
        ) : null}
        {problem ? <ProblemBox problem={problem} planningHref={planningHref} onReload={onReload} /> : null}
      </header>

      <div className={styles.unsure}>
        <section
          className={styles.field}
          aria-label="Original"
          // Narrow, the field takes the sheet's own proportion (CSS): a
          // landscape page in a tall box pushed the choice off the phone.
          style={{ "--page-ratio": aspect } as CSSProperties}
        >
          <div className={styles.fieldHead}>
            <h2 className="label">Original</h2>
            <span className={styles.fieldNote}>{shown ? `mit ${candidateName(shown)}` : sheetLabel}</span>
          </div>
          <div className={styles.fieldBody}>
            <OriginalPane
              image={image}
              page={review}
              walls={[]}
              activeWallId={null}
              simplifications={again ? [] : review.simplifications}
              outline={shown ? { points: shown.points, closed: shown.closed } : null}
            />
          </div>
        </section>

        <section className={styles.readings} aria-labelledby="form-readings">
          <h2 id="form-readings" className="label">
            Lesarten
          </h2>
          {cards.length > 0 ? (
            <>
              <div
                className={styles.cards}
                role="radiogroup"
                aria-labelledby="form-readings"
                onKeyDown={onKeyDown}
                onPointerLeave={() => setPreviewed(null)}
              >
                {cards.map(({ candidate, position }) => (
                  <button
                    key={candidate.label}
                    ref={(element) => {
                      if (element) cardRefs.current.set(position, element);
                      else cardRefs.current.delete(position);
                    }}
                    type="button"
                    role="radio"
                    aria-checked={position === chosen}
                    tabIndex={position === tabStop ? 0 : -1}
                    className={styles.card}
                    data-position={position}
                    onClick={() => {
                      setChosen(position);
                      setProblem(null);
                    }}
                    onPointerEnter={() => setPreviewed(position)}
                    onFocus={() => setPreviewed(position)}
                    onBlur={() => setPreviewed((was) => (was === position ? null : was))}
                  >
                    {/* The reading's shape, drawn — a sheet (design
                        2026-10-24 §5.3) on warm paper (B-20); the card
                        around it in the mode. */}
                    <svg
                      className={styles.cardShape}
                      data-blatt="zeichnung"
                      viewBox={`0 0 ${CARD_W} ${CARD_H}`}
                      width={CARD_W}
                      height={CARD_H}
                      aria-hidden="true"
                    >
                      <polyline points={cardPoints(candidate, aspect)} />
                    </svg>
                    <span className={styles.cardText}>
                      <span className={styles.cardName}>
                        <span className={styles.radio} aria-hidden="true" />
                        {candidateName(candidate)}
                      </span>
                      <span className={styles.cardNote}>{candidateLine(candidate, total)}</span>
                      {position === holding ? <span className={styles.cardHolding}>gilt gerade</span> : null}
                    </span>
                  </button>
                ))}
              </div>
              <p className={styles.votesNote}>{VOTES_NOTE}</p>
            </>
          ) : null}

          {cards.length > 0 ? (
            <>
              <div className={styles.unsureMain}>
                {staying && backHref !== null ? (
                  <Link className={styles.primaryLink} to={backHref}>
                    Bei {candidateName(review.candidates[0]!)} bleiben
                  </Link>
                ) : (
                  // The one main action of the choice, in K60 red (design
                  // 2026-10-24 §8.2).
                  <Button
                    variant="primary"
                    busy={sending}
                    aria-disabled={chosen === null ? "true" : undefined}
                    aria-describedby={chosen === null ? reasonId : undefined}
                    data-role="choose-form"
                    onClick={() => void take()}
                  >
                    {sending ? "Startet …" : "Diese Lesart nehmen — Maße lesen"}
                  </Button>
                )}
                {chosen === null ? (
                  <p id={reasonId} className={styles.chooseReason}>
                    Wähl zuerst eine Lesart.
                  </p>
                ) : null}
              </div>
              <div className={styles.unsureActions}>
                {/* Vorgang 2026-0009 (19.10.2026): this way led into Stand 1
                    without a single measure. It opens the form only — the
                    measures of a reading are read once it is taken, and
                    „Im Editor bearbeiten“ on that review carries them along. */}
                {chosenCandidate !== null ? (
                  <Link
                    className={styles.secondaryLink}
                    to={editorHref(chosen!)}
                    aria-describedby={WITHOUT_MEASURES}
                  >
                    Ohne Maße im Editor öffnen
                  </Link>
                ) : (
                  <Button variant="linie" aria-disabled="true" aria-describedby={reasonId}>
                    Ohne Maße im Editor öffnen
                  </Button>
                )}
                {otherPage !== null ? (
                  <Link className={`inline-link ${styles.quietWay}`} to={otherPage}>
                    Andere Seite wählen
                  </Link>
                ) : null}
                <Link className={`inline-link ${styles.quietWay}`} to={drawHref}>
                  Selbst zeichnen
                </Link>
              </div>
              <p id={WITHOUT_MEASURES} className={styles.chooseReason}>
                Maße liest die Maschine erst zu einer genommenen Lesart. Im Editor ohne Maße tippst du
                mindestens eine Länge selbst — sonst wird daraus kein Stand.
              </p>
            </>
          ) : (
            <div className={styles.unsureActions}>
              <Link className={styles.primaryLink} to={drawHref}>
                Selbst zeichnen
              </Link>
              {otherPage !== null ? (
                <Link className={styles.secondaryLink} to={otherPage}>
                  Andere Seite wählen
                </Link>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ProblemBox({ problem, planningHref, onReload }: { problem: Problem; planningHref: string; onReload: () => void }) {
  const reload = (
    <Button variant="leise" flush onClick={onReload}>
      Neu laden
    </Button>
  );
  switch (problem.kind) {
    case "stand":
      return (
        <StateMessage
          tone="conflict"
          live="alert"
          title="Für diesen Vorgang gibt es schon einen Stand."
          action={
            <Link className="inline-link" to={planningHref}>
              Zur Planung
            </Link>
          }
        >
          Eine neue Lesung würde ihn nicht ersetzen.
        </StateMessage>
      );
    case "stale":
      return <StateMessage tone="conflict" live="alert" title="Inzwischen liegt eine neuere Lesung vor." action={reload} />;
    case "checked":
      return (
        <StateMessage tone="conflict" live="alert" title="Der Umriss dieser Lesung ist schon geprüft." action={reload}>
          Es gibt keine Lesart zu wählen.
        </StateMessage>
      );
    case "unknown_candidate":
      return (
        <StateMessage tone="conflict" live="alert" title="Diese Lesart gibt es in der Lesung nicht." action={reload}>
          Lade neu und wähl noch einmal.
        </StateMessage>
      );
    case "other":
      return (
        <StateMessage tone="conflict" live="alert" title="Nicht gestartet." action={reload}>
          {problem.detail}
        </StateMessage>
      );
  }
}
